<template>
  <el-config-provider :locale="zhCn">
  <div id="app" :class="{ 'is-standalone': isStandalone }">
    <header v-if="!isDocSharePage" class="app-header">
      <nav class="navbar">
        <div class="container nav-container">
          <button class="nav-hamburger" @click="drawerVisible = true" aria-label="菜单">
            <el-icon><Menu /></el-icon>
          </button>
          <router-link to="/" class="logo">
            <el-icon class="logo-icon"><Tools /></el-icon>
            <span>工具集</span>
          </router-link>
          <div class="nav-links desktop-nav-links">
            <router-link to="/">首页</router-link>
            <router-link to="/categories">分类</router-link>
            <router-link to="/tools">所有工具</router-link>
            <router-link to="/docs">技术文档</router-link>
            <a href="http://www.cgh123.com:5212" target="_blank" rel="noopener">网盘</a>
          </div>
          <!-- 桌面端用户区：管理员可见管理入口；登录/退出始终可用 -->
          <div class="nav-user desktop-nav-user">
            <router-link v-if="userStore.isLoggedIn && userStore.isAdmin" to="/admin">管理</router-link>
            <router-link v-if="!userStore.isLoggedIn" to="/login">登录</router-link>
            <a v-else @click="logout">退出</a>
          </div>
          <!-- 移动端占位，保持标题居中 -->
          <span class="nav-spacer-mobile"></span>
        </div>
      </nav>
    </header>

    <main v-if="!isDocSharePage" class="container app-main">
      <router-view v-slot="{ Component }">
        <transition name="route-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <router-view v-else v-slot="{ Component }">
      <component :is="Component" />
    </router-view>

    <footer v-if="!isDocSharePage" class="app-footer">
      <div class="container">
        <p>© {{ currentYear }} 工具集网站 | 使用 Python + Vue 构建</p>
      </div>
    </footer>

    <!-- 移动端底部 Tab Bar：主要入口，接近 APP 体验 -->
    <nav v-if="!isDocSharePage" class="mobile-tabbar">
      <router-link to="/">
        <el-icon class="tb-icon"><HomeFilled /></el-icon>
        <span>首页</span>
      </router-link>
      <router-link to="/categories">
        <el-icon class="tb-icon"><Folder /></el-icon>
        <span>分类</span>
      </router-link>
      <router-link to="/tools">
        <el-icon class="tb-icon"><SetUp /></el-icon>
        <span>工具</span>
      </router-link>
      <router-link to="/docs">
        <el-icon class="tb-icon"><Document /></el-icon>
        <span>文档</span>
      </router-link>
      <router-link v-if="userStore.isAdmin" to="/admin">
        <el-icon class="tb-icon"><Setting /></el-icon>
        <span>管理</span>
      </router-link>
    </nav>

    <!-- 移动端抽屉菜单：承载全部入口（含管理/登录/网盘） -->
    <el-drawer v-model="drawerVisible" direction="ltr" size="78%" :with-header="false">
      <div class="drawer-wrap">
        <div class="drawer-header">
          <span class="drawer-logo">🔧 工具集</span>
          <button class="drawer-close" @click="drawerVisible = false" aria-label="关闭">
            <el-icon><Close /></el-icon>
          </button>
        </div>
        <div class="drawer-links">
          <router-link to="/" @click="drawerVisible = false">
            <el-icon><HomeFilled /></el-icon><span>首页</span>
          </router-link>
          <router-link to="/categories" @click="drawerVisible = false">
            <el-icon><Folder /></el-icon><span>分类</span>
          </router-link>
          <router-link to="/tools" @click="drawerVisible = false">
            <el-icon><SetUp /></el-icon><span>所有工具</span>
          </router-link>
          <router-link to="/docs" @click="drawerVisible = false">
            <el-icon><Document /></el-icon><span>技术文档</span>
          </router-link>
          <a href="http://www.cgh123.com:5212" target="_blank" rel="noopener">
            <el-icon><Link /></el-icon><span>网盘</span>
          </a>
          <router-link v-if="userStore.isLoggedIn && userStore.isAdmin" to="/admin" @click="drawerVisible = false">
            <el-icon><Setting /></el-icon><span>管理</span>
          </router-link>
          <router-link v-if="!userStore.isLoggedIn" to="/login" @click="drawerVisible = false">
            <el-icon><Key /></el-icon><span>登录</span>
          </router-link>
          <a v-else @click="logout">
            <el-icon><SwitchButton /></el-icon><span>退出</span>
          </a>
        </div>
        <div class="drawer-footer">© {{ currentYear }} 工具集</div>
      </div>
    </el-drawer>
  </div>
  </el-config-provider>
</template>

<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router'
import { computed, ref, watch } from 'vue'
import {
  HomeFilled, Menu, Close, Tools, Folder, SetUp,
  Document, Setting, Link, Key, SwitchButton
} from '@element-plus/icons-vue'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { useUserStore } from './stores/user'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const drawerVisible = ref(false)
const currentYear = new Date().getFullYear()

const isDocSharePage = computed(() => route.name === 'docShare')

// 是否以 PWA 独立模式运行（添加到主屏幕后）
const isStandalone = computed(() => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
})

// 路由切换时关闭抽屉，避免返回后仍打开
watch(() => route.fullPath, () => {
  drawerVisible.value = false
})

const logout = () => {
  drawerVisible.value = false
  userStore.logout()
  router.push('/login')
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: #f8f9fa;
  color: #333;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ============ 顶部导航栏 ============ */
.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  padding-top: var(--safe-top);
  background: var(--brand-gradient);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.navbar {
  color: white;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: var(--nav-h);
  padding-top: 0;
  padding-bottom: 0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.4rem;
  font-weight: bold;
  color: white;
  text-decoration: none;
  white-space: nowrap;
}
.logo-icon {
  font-size: 1.3rem;
}

.nav-hamburger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 22px;
  cursor: pointer;
  transition: background 0.2s;
}
.nav-hamburger:active {
  background: rgba(255, 255, 255, 0.3);
}

.nav-spacer-mobile {
  display: none;
  width: 40px;
}

.nav-links a {
  color: white;
  margin-left: 1.5rem;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.3s;
  cursor: pointer;
}

.nav-links a:hover {
  opacity: 0.8;
}

/* 桌面端用户区（登录/退出/管理） */
.nav-user {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: 1.5rem;
  padding-left: 1.5rem;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
}
.nav-user a {
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.3s;
  white-space: nowrap;
}
.nav-user a:hover {
  opacity: 0.8;
}

/* 移动端隐藏用户区（由抽屉菜单承载） */
@media (max-width: 768px) {
  .nav-user {
    display: none;
  }
}

main.app-main {
  min-height: calc(100vh - 140px);
  padding: 2rem 0;
}

.app-footer {
  background: #2c3e50;
  color: #ecf0f1;
  text-align: center;
  padding: 1.5rem 0;
  margin-top: 2rem;
  padding-bottom: calc(1.5rem + var(--safe-bottom));
}

/* ============ 移动端导航布局 ============ */
@media (max-width: 768px) {
  .nav-hamburger {
    display: flex;
  }
  .nav-spacer-mobile {
    display: block;
  }
  .logo {
    font-size: 1.2rem;
  }
  main.app-main {
    padding: 1rem 0;
  }
}

/* ============ 抽屉菜单样式 ============ */
.drawer-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: var(--safe-top);
}
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  background: var(--brand-gradient);
  color: #fff;
}
.drawer-logo {
  font-size: 1.3rem;
  font-weight: bold;
}
.drawer-close {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drawer-links {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  overflow-y: auto;
}
.drawer-links a {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 22px;
  color: #303133;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s;
}
.drawer-links a .el-icon {
  font-size: 18px;
  color: #909399;
}
.drawer-links a.router-link-active .el-icon {
  color: var(--el-color-primary);
}
.drawer-links a:hover,
.drawer-links a:active {
  background: #f5f7fa;
}
.drawer-links a.router-link-active {
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}
.drawer-footer {
  padding: 16px 22px;
  color: #909399;
  font-size: 12px;
  padding-bottom: calc(16px + var(--safe-bottom));
}
</style>
